import dagre, { graphlib } from '@dagrejs/dagre';
import { assert_array_typeof_string, assert_not_null, assert_true } from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { DTO_GRAPH__instance } from '../DTO_GRAPH__instance';

declare module '../DTO_GRAPH__instance' {
  interface DTO_GRAPH__instance {
    dagre__convert_to(): graphlib.Graph;

    /*
     *
     */
    dagre__get_nodes_ids_to__all(graph_dagre: graphlib.Graph, node_id: string): string[];

    dagre__get_nodes_ids_to(graph_dagre: graphlib.Graph, node_id: string): string[];

    /*
     *
     */
    dagre__get_nodes_ids_from__all(graph_dagre: graphlib.Graph, node_id: string): string[];

    dagre__get_nodes_ids_from(graph_dagre: graphlib.Graph, node_id: string): string[];

    /*
     *
     */
    dagre__get_connected_ids__all(graph_dagre: graphlib.Graph, node_id: string): string[];

    dagre__get_connected_ids(graph_dagre: graphlib.Graph, node_id: string): string[];

    dagre__sort_fields(fields: FIELD__base[]): FIELD__base[];

    dagre__nodes_are_connected(graph_dagre: graphlib.Graph, node_id_a: string, node_id_b: string): boolean;

    dagre__autolayout(): void;
  }
}

export function INIT_EXTENSION_DTO_GRAPH__instance_dagre() {
  // do nothing
}

/*
 * CREATE
 */
DTO_GRAPH__instance.prototype.dagre__convert_to = function (): graphlib.Graph {
  const out = new graphlib.Graph();

  out.setDefaultEdgeLabel(() => ({}));

  for (const invocation of this.nodes__get_all()) {
    const [width, height] = [invocation.meta.graph_width_px, invocation.meta.graph_height_px];

    out.setNode(invocation.meta.node_id, { width, height });
  }

  for (const edge of this.edges) {
    out.setEdge(`${edge.field_from__node_id}`, `${edge.field_to__node_id}`);
  }

  return out;
};

/*
 *
 */
DTO_GRAPH__instance.prototype.dagre__get_nodes_ids_to__all = function (graph_dagre: graphlib.Graph, node_id: string): string[] {
  if (this.nodes_map.size() < 2) return [];

  const out_set = new Set<string>();
  const queue: string[] = [node_id];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const direct_preds = graph_dagre.predecessors(current) || [];

    for (const pred of direct_preds) {
      if (out_set.has(pred)) continue;

      out_set.add(pred);
      queue.push(pred); // Queue it up to find ITS predecessors
    }
  }

  const out = Array.from(out_set);
  return out;
};

DTO_GRAPH__instance.prototype.dagre__get_nodes_ids_to = function (graph_dagre: graphlib.Graph, node_id: string): string[] {
  if (this.nodes_map.size() < 2) return [];

  let out_list = graph_dagre.predecessors(node_id) || []; //dagre returns undefined if no predecessors
  assert_array_typeof_string(out_list);

  return new Set(out_list).to_array();
};

/*
 *
 */
DTO_GRAPH__instance.prototype.dagre__get_nodes_ids_from__all = function (graph_dagre: graphlib.Graph, node_id: string): string[] {
  if (this.nodes_map.size() < 2) return [];

  const out_set = new Set<string>();
  const queue: string[] = [node_id];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const direct_succs = graph_dagre.successors(current) || [];

    for (const succ of direct_succs) {
      if (out_set.has(succ)) continue;

      out_set.add(succ);
      queue.push(succ); // Queue it up to find ITS successors
    }
  }

  const out = Array.from(out_set);
  return out;
};

DTO_GRAPH__instance.prototype.dagre__get_nodes_ids_from = function (graph_dagre: graphlib.Graph, node_id: string): string[] {
  if (this.nodes_map.size() < 2) return [];

  let out_list = graph_dagre.successors(node_id) || []; //dagre returns undefined if no predecessors
  assert_array_typeof_string(out_list);

  return new Set(out_list).to_array();
};

/*
 *
 */

DTO_GRAPH__instance.prototype.dagre__get_connected_ids__all = function (graph_dagre: graphlib.Graph, node_id: string): string[] {
  let predecessors = this.dagre__get_nodes_ids_to__all(graph_dagre, node_id);
  let successors = this.dagre__get_nodes_ids_from__all(graph_dagre, node_id);

  let connected_ids_all = [...predecessors, ...successors];

  return new Set(connected_ids_all).to_array();
};

DTO_GRAPH__instance.prototype.dagre__get_connected_ids = function (graph_dagre: graphlib.Graph, node_id: string): string[] {
  let predecessors = this.dagre__get_nodes_ids_to(graph_dagre, node_id);
  let successors = this.dagre__get_nodes_ids_from(graph_dagre, node_id);

  let connected_ids_all = [...predecessors, ...successors];

  return new Set(connected_ids_all).to_array();
};

DTO_GRAPH__instance.prototype.dagre__sort_fields = function (fields: FIELD__base[]): FIELD__base[] {
  const out: FIELD__base[] = [];
  const graph_dagre = new graphlib.Graph({ directed: true });

  // 1. Create a quick lookup map for your input fields using the same key format
  // This avoids running a nested .find() loop inside your later iterations (O(N) vs O(N^2))
  const fields_map = new Map<string, FIELD__base>();

  for (const field of fields) {
    const key = `${field.meta.node_id}:${field.meta.field_id}`;
    fields_map.set(key, field);
  }

  // 2. Build the dependency graph from your edges
  for (const edge of this.edges) {
    const key_from = `${edge.field_from__node_id}:${edge.field_from__field_id}`;
    const key_to = `${edge.field_to__node_id}:${edge.field_to__field_id}`;

    graph_dagre.setEdge(key_from, key_to);
  }

  // 3. Get the strictly ordered list of connected fields from left to right
  // Wrap this in a try/catch in case a user accidentally introduces a cycle
  let keys_sorted: string[] = graphlib.alg.topsort(graph_dagre);

  // 4. Push the sorted fields into the output array and remove them from our map
  for (const key of keys_sorted) {
    if (!fields_map.has(key)) continue;

    const field = fields_map.get_or_assert(key);

    out.push(field);

    fields_map.delete(key); // Mark as processed
  }

  // 5. CRITICAL STEP: Append any remaining disconnected fields
  // Fields with no edges won't be picked up by topsort, but they still need to be updated!
  for (const field of fields_map.values()) out.push(field);

  return out;
};

DTO_GRAPH__instance.prototype.dagre__nodes_are_connected = function (graph_dagre: graphlib.Graph, node_id_a: string, node_id_b: string): boolean {
  assert_true(this.nodes_map.has(node_id_a));
  assert_true(this.nodes_map.has(node_id_b));

  let connected_ids = this.dagre__get_connected_ids__all(graph_dagre, node_id_a);

  return connected_ids.includes(node_id_b);
};

DTO_GRAPH__instance.prototype.dagre__autolayout = function () {
  const graph_dagre = this.dagre__convert_to();

  let { GRAPH_GRID_NODES_GAP_X, GRAPH_GRID_NODES_GAP_Y, GRID_CELL_SIZE } = CONSTANTS__shared;

  const [marginx, marginy] = [GRID_CELL_SIZE * GRAPH_GRID_NODES_GAP_X, GRID_CELL_SIZE * GRAPH_GRID_NODES_GAP_Y];

  graph_dagre.setGraph({
    rankdir: 'LR',
    //
    nodesep: marginy,
    ranksep: marginx,
    //
    marginx,
    marginy,
    //
    align: 'UL',
    //
    ranker: 'longest-path', // 'network-simplex' Gives a more balanced, standard layout than 'longest-path'
    acyclicForced: true, // Forces cycle breaking if you have feedback loops
  });

  dagre.layout(graph_dagre);

  for (const node of this.nodes__get_all()) {
    const node_dagre = graph_dagre.node(node.meta.node_id);
    assert_not_null(node_dagre);

    let [width, height] = [node.meta.graph_width_px, node.meta.graph_height_px];

    let [x, y] = [node_dagre.x - width / 2, node_dagre.y - height / 2];
    [x, y] = [Math.round(x), Math.round(y)];

    [node.meta.graph_x_px, node.meta.graph_y_px] = [x, y];
  }
};
